/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WorkOrdersApiService from '../../Services/WorkOrdersService';
import { isEmpty } from 'lodash';
import { AlertColor, CircularProgress } from '@mui/material';
import moment from 'moment';

import theme from '../../../../Shared/Themes/theme';
import {
  TaskAssignment,
  WorkDayTeamForWorkOrderTeamWithAssignment,
  WorkOrder,
  WorkOrderTeam,
  WorkOrderTeamWithAssignment,
} from '../../../../API';
import { DateFormat } from '../../../../Shared/Constants/App';
import {
  fetchDataFromS3Bucket,
  formatDate,
  getAuthData,
  getStoredCustomerDetails,
} from '../../../../Shared/Utilities/utils';
import SupervisorPunchIn from './SupervisorPunchIn';
import { ITeamMemberData } from '../JobDetails/JobAssignmentCard';
import SnackbarMessage from '../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';
import { AssociateOnTheJobCard, LoaderTag } from '../ActionItems/ActionItems.Style';
import { AssignmentTeamCard } from '../JobDetails/OneTimeJobDetails.Style';
import { FacilityInterface } from '../../../Company/Models/Company.Model';
import { AllShiftDetailsQuery, SchedulesByDateQuery } from './AssociateCard';
import { IGraphQLResponse } from '../../../../Shared/Models';
import { IEmployees } from './AssociateOnTheJobAccordion';

interface AssociateOnTheJobData {
  jobData?: WorkOrder;
  FacilityData?: FacilityInterface;
  jobId: string;
  jobsLoader?: boolean;
  specificDate?: any;
  workOrderScheduleId: string;
  woScheduleMasterId: string;
  scheduleData: SchedulesByDateQuery;
  shiftData: AllShiftDetailsQuery[];
  workOrderResponseData: any;
  employees: IEmployees[];
  isPrimeVendor?: boolean;
  originalWorkOrderId: string;
}

export interface WorkOrderTeamData {
  teamId: string;
  vendorId?: string | null;
  userId?: string | null;
  roleId?: string | null;
  isSupervisor?: boolean | null;
  workOrderId?: string | null;
  woScheduleMasterId?: string | null;
  taskAssignments?: Array<TaskAssignment | null> | null;
  associateStartDate?: string | null;
  associateEndDate?: string | null;
  cognitoUserId?: string | null;
  workDayId?: string | null;
  noShow?: boolean | null;
  cantMakeIt?: boolean | null;
  jobAccepted?: boolean | null;
  workDayTeam?: WorkDayTeamForWorkOrderTeamWithAssignment | null;
  username?: string | null;
  roleName?: string | null;
  id: string | null | undefined;
  isCannotMakeJob?: boolean;
  isChecked: boolean;
  isAlreadyChecked?: boolean | null;
  isDisabled?: boolean;
  isDisabledMoreActions?: boolean;
  isJobNotAccepted?: boolean;
  name: string | null | undefined;
  profileImg: string;
  profileType?: string;
  punchType?: string;
  role?: string | null;
  punchInTime?: string;
  punchOutTime?: string;
}

const AssociateOnTheJob = ({
  jobData,
  FacilityData,
  jobId,
  jobsLoader,
  specificDate,
  woScheduleMasterId,
  workOrderScheduleId,
  scheduleData,
  shiftData,
  workOrderResponseData,
  employees,
  isPrimeVendor,
  originalWorkOrderId,
}: AssociateOnTheJobData) => {
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('success');
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [teamData, setTeamData] = useState<WorkOrderTeamData[]>([]);
  const [associateTeamData, setAssociateTeamData] = useState<WorkOrderTeamData[]>([]);
  const [checkedAssociates, setCheckedAssociates] = useState<any>([]);

  const [isTeamDataLoading, setIsTeamDataLoading] = useState<boolean>(false);
  const [workOrderId, setWorkOrderId] = useState<string>('');
  const [workOrderData, setWorkOrderData] = useState<any>({});
  const [isAssociatePunchedIn, setIsAssociatePunchedIn] = useState(false);
  const [jobEndDate, setJobEndDate] = useState<string>('');
  const [workDayId, setWorkDayId] = useState('');
  const [isFutureShiftSelected, setIsFutureShiftSelected] = useState<boolean>(false);

  const showSnackbar = (severity: AlertColor, message: string) => {
    setSnackbarOpen(true);
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
  };

  const fetchVendorTeam = async (workOrderTeamResponse?: IGraphQLResponse) => {
    const customerDetails: any = getStoredCustomerDetails();
    const vendorId = customerDetails?.customerId;
    const { userId } = getAuthData();

    if (!userId && !vendorId) return;

    setIsTeamDataLoading(true);
    try {
      const supervisorDetails = getSupervisorDetails(workOrderTeamResponse?.data);
      if (!isEmpty(supervisorDetails)) {
        const modifiedData = await getModifiedData(supervisorDetails);
        setTeamData(modifiedData.teamData);
      } else {
        setTeamData([]);
      }
      setIsTeamDataLoading(false);
    } catch (e) {
      console.error('Error', e);
      setTeamData([]);
      setIsTeamDataLoading(false);
    }
  };

  const getModifiedData = async (vendorTeamsUsersExcludingAssociates: any[]) => {
    const tempCheckedMember: WorkOrderTeamData[] = [];

    const modifiedData = await Promise.all(
      vendorTeamsUsersExcludingAssociates.map(async (member: WorkOrderTeamWithAssignment) => {
        const memberProfileUrl = await fetchDataFromS3Bucket(
          navigate,
          `associate/${member?.userId}/profile-pictures/${member?.userId}`
        );
        const tempMember = {
          id: member.userId,
          userId: member.userId,
          name: member.username,
          isChecked: false,
          profileImg: memberProfileUrl,
          role: member?.roleName,
          roleId: member?.roleId,
          isSupervisor: member?.isSupervisor,
          teamId: member?.teamId,
        };

        if (member) {
          if (member?.isSupervisor) {
            const checkedMember = {
              ...tempMember,
              isChecked: true,
              teamId: member?.teamId || '',
            };
            tempCheckedMember.push(checkedMember);
            return checkedMember;
          }
          return { ...tempMember, teamId: member?.teamId || '' };
        }
        return tempMember;
      })
    );
    return { teamData: modifiedData, checkedSupervisors: tempCheckedMember };
  };

  const fetchAssociateTeam = async (
    workOrderId: string,
    workOrderTeamResponse?: IGraphQLResponse,
    employees?: IEmployees[]
  ) => {
    const customerDetails = getStoredCustomerDetails();
    const vendorId = customerDetails?.customerId;
    const { userId } = getAuthData();
    const onBehalfOfAssociate = false;

    if (!userId && !vendorId) return;

    setIsTeamDataLoading(true);
    try {
      const associateDetails = getAssociateDetails(workOrderTeamResponse?.data);

      const assignedAssociateDetails = getAssignedAssociateDetails(associateDetails, employees);
      const { modifiedAssociateTeam } = await processAssociateTeam(assignedAssociateDetails);

      const finalData = await processFinalData(modifiedAssociateTeam, onBehalfOfAssociate);

      const filteredAssociates = finalData?.checkedAssociates?.filter((associate) => !associate.isSupervisor);
      setCheckedAssociates(filteredAssociates);

      setAssociateTeamData(finalData.modifiedData);
      setIsTeamDataLoading(false);
    } catch (e) {
      console.error('Error', e);
      setAssociateTeamData([]);
      setIsTeamDataLoading(false);
    }
  };

  const getSupervisorDetails = (data: WorkOrderTeam[]) => {
    return data.filter((member) => member.isSupervisor);
  };

  const getAssociateDetails = (data: any[]) => {
    return data.filter((member) => !member.isSupervisor);
  };
  const getAssignedAssociateDetails = (data: any[], employees?: IEmployees[]) => {
    if (!employees) return [];
    return data
      .map((member) => {
        const employee = Array.isArray(employees)
          ? employees.find((emp: { userId: string }) => emp.userId === member.userId)
          : undefined;
        if (employee) {
          return { ...member, workDayId: employee.empShiftId };
        }
        return null;
      })
      .filter(Boolean);
  };

  const processAssociateTeam = async (workOrderTeamData: any[]) => {
    const modifiedAssociateTeam = await Promise.all(
      workOrderTeamData.map(async (workOrderTeamMember) => {
        const memberProfileUrl = await fetchDataFromS3Bucket(
          navigate,
          `associate/${workOrderTeamMember?.userId}/profile-pictures/${workOrderTeamMember?.userId}`
        );

        return {
          id: workOrderTeamMember?.userId ?? '',
          userId: workOrderTeamMember?.userId ?? '',
          name: workOrderTeamMember?.username,
          isChecked: false,
          profileImg: memberProfileUrl,
          role: workOrderTeamMember?.roleName,
          roleId: workOrderTeamMember?.roleId,
          isSupervisor: workOrderTeamMember?.isSupervisor,
          isDisabled: false,
          isJobNotAccepted: false,
          associateStartDate: workOrderTeamMember?.associateStartDate ?? '',
          associateEndDate: workOrderTeamMember?.associateEndDate ?? '',
          workDayId: workOrderTeamMember?.workDayId,
        };
      })
    );
    return { modifiedAssociateTeam };
  };
  const processFinalData = async (data: any[], onBehalfOfAssociate: boolean) => {
    const tempCheckedMember: ITeamMemberData[] = [];
    const modifiedData = await Promise.all(
      data.map(async (member) => {
        const tempMember = { ...member };
        let punchType;
        let punchInTime;
        let punchOutTime;
        if (member?.workDayId) {
          const latestPunchInOutByWorkDayResponse = await WorkOrdersApiService.getLatestPunchInOutByWorkDay(
            member?.workDayId,
            member?.userId
          );
          punchType = latestPunchInOutByWorkDayResponse.data?.punchType;
          punchInTime = latestPunchInOutByWorkDayResponse?.data?.punchInTime ?? '';
          punchOutTime = latestPunchInOutByWorkDayResponse?.data?.punchOutTime ?? '';
        }

        if (member) {
          const checkedMember = {
            ...tempMember,
            isChecked: true,
            isDisabled: onBehalfOfAssociate,
            teamId: member?.teamId || '',
            isSupervisor: member?.isSupervisor,
            workDayId: member?.workDayId,
            punchType: punchType,
            isJobNotAccepted: member?.jobAccepted,
            isCannotMakeJob: member?.cantMakeIt,
            punchInTime: punchInTime,
            punchOutTime: punchOutTime,
          };
          tempCheckedMember.push(checkedMember);
          return checkedMember;
        }
        return tempMember;
      })
    );
    return { modifiedData, checkedAssociates: tempCheckedMember };
  };

  const getPunchInDetails = async (workOrderTeamResponse?: any) => {
    const associateDetails = workOrderTeamResponse?.find((member: any) => !member.isSupervisor);
    const workDayId = associateDetails?.workDayTeam?.workDayId;
    if (workDayId) {
      const latestPunchInOutResponse = await WorkOrdersApiService.getLatestPunchRecords(workDayId);
      if (!isEmpty(latestPunchInOutResponse?.data) && isEmpty(latestPunchInOutResponse?.data?.errorCode)) {
        setIsAssociatePunchedIn(!!latestPunchInOutResponse?.data?.punchType);
      }
    }
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const getWorkOrderDetails = async () => {
    setIsDataLoading(true);
    try {
      if (jobData?.jobId && specificDate && !isEmpty(workOrderResponseData)) {
        const workOrderResponseByJobId = workOrderResponseData?.filter(
          (data: { jobId: string }) => data?.jobId === jobData?.jobId
        );

        if (!isEmpty(workOrderResponseData ?? [])) {
          const workOrderData = workOrderResponseData?.[0];
          setWorkOrderId(workOrderResponseByJobId?.[0]?.workOrderId);

          const endDate = new Date(workOrderResponseByJobId?.[0]?.jobEndDate);
          const formattedEndDate = formatDate(endDate.toISOString());
          setJobEndDate(formattedEndDate);

          setWorkOrderData(workOrderData);

          const scheduleDate = formatDate(specificDate, 'YYYY-MM-DD');
          const workOrderTeamResponse = await WorkOrdersApiService.getWorkOrderTeams({
            workOrderId: originalWorkOrderId,
            woScheduleMasterId: woScheduleMasterId,
            fromDate: scheduleDate,
            toDate: scheduleDate,
          });
          await getPunchInDetails(workOrderTeamResponse?.data);
          if (!isEmpty(workOrderTeamResponse?.data ?? [])) {
            const workDayId = workOrderTeamResponse?.data[0]?.workDayId ?? '';
            setWorkDayId(workDayId);
            fetchVendorTeam(workOrderTeamResponse);
            fetchAssociateTeam(workOrderData?.workOrderId, workOrderTeamResponse, employees);
          } else if (!isEmpty(workOrderTeamResponse?.errors ?? [])) {
            setTeamData([]);
            setIsTeamDataLoading(false);
            setAssociateTeamData([]);
            setCheckedAssociates([]);
            showSnackbar(workOrderTeamResponse?.errors?.[0]?.message ?? '', 'error');
          } else {
            setTeamData([]);
            setIsTeamDataLoading(false);
            setAssociateTeamData([]);
            setCheckedAssociates([]);
          }
        }
      }
      setIsDataLoading(false);
    } catch (e) {
      console.log('error', e);
      setIsDataLoading(false);
    }
  };

  useEffect(() => {
    const currentTime = moment().toJSON();
    const isCurrentShiftIsInFuture = moment(
      moment(specificDate).local().format(DateFormat.ISODate),
      DateFormat.ISODate
    ).isAfter(moment(currentTime, DateFormat.ISODate));
    if (isCurrentShiftIsInFuture) {
      setIsFutureShiftSelected(true);
    } else {
      setIsFutureShiftSelected(false);
    }
  }, [specificDate]);

  useEffect(() => {
    if (!isEmpty(specificDate) && !isEmpty(jobId)) {
      getWorkOrderDetails();
    }
  }, [specificDate, jobId]);

  return (
    <>
      {jobsLoader ? (
        <LoaderTag>
          <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
        </LoaderTag>
      ) : (
        <>
          <AssociateOnTheJobCard>
            <AssignmentTeamCard>
              {workOrderId && (
                <SupervisorPunchIn
                  teamData={teamData}
                  associateTeamData={associateTeamData}
                  isTeamDataLoading={isTeamDataLoading || isDataLoading}
                  workOrderId={workOrderId}
                  workOrderData={workOrderData}
                  address={FacilityData?.address}
                  isAssociatePunchedIn={isAssociatePunchedIn}
                  workDayId={workDayId}
                  isFutureShiftSelected={isFutureShiftSelected}
                  specificDate={specificDate}
                  jobEndDate={jobEndDate}
                  getWorkOrderDetails={getWorkOrderDetails}
                  woScheduleMasterId={woScheduleMasterId}
                  workOrderScheduleId={workOrderScheduleId}
                  scheduleData={scheduleData}
                  shiftData={shiftData}
                  checkedAssociates={checkedAssociates}
                  isPrimeVendor={isPrimeVendor}
                />
              )}
            </AssignmentTeamCard>
            <SnackbarMessage
              open={snackbarOpen}
              successMessage={snackbarMessage}
              errorMessage={snackbarMessage}
              severity={snackbarSeverity}
              onClose={() => setSnackbarOpen(false)}
            />
          </AssociateOnTheJobCard>
        </>
      )}
    </>
  );
};

export default AssociateOnTheJob;
